'use client';

import Script from 'next/script';
import { useMemo } from 'react';
import './seo-styles.css';

export function SeoView({
  content,
  script,
  className,
  doNotUseHeading,
}: {
  content: string;
  script?: string;
  className?: string;
  showAll?: boolean;
  doNotUseHeading?: boolean;
}) {
  const { sanitizedHtml, heading } = useMemo(() => {
    const sanitized = content?.replace(/style="[^"]*"/gi, '') || '';
    if (!sanitized) {
      return { sanitizedHtml: '', heading: '' };
    }

    const match = sanitized.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
    if (!match) {
      return { sanitizedHtml: sanitized, heading: '' };
    }

    if (doNotUseHeading) {
      const withoutH1 = sanitized.replace(/<h1[^>]*>[\s\S]*?<\/h1>/gi, '');
      return { sanitizedHtml: withoutH1, heading: '' };
    }

    const headingInnerHtml = match[1] ?? '';
    const headingText = headingInnerHtml.replace(/<[^>]*>/g, '').trim();
    const withoutHeading = sanitized.replace(match[0], '');

    return { sanitizedHtml: withoutHeading, heading: headingText };
  }, [content, doNotUseHeading]);

  if (!heading && sanitizedHtml === '') {
    return script && script !== '' ? (
      <section className="mb-12">
        <Script
          id="json-ld-seo"
          type="application/ld+json"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{ __html: script }}
        />
      </section>
    ) : null;
  }

  return (
    <section className="mb-12">
      {script && script !== '' && (
        <Script
          id="json-ld-seo"
          type="application/ld+json"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: script,
          }}
        />
      )}
      {heading && (
        <h1
          className={`seo-content mt-6 px-4 md:px-0 text-[.875rem] ${className ?? ''} sm:text-[1.125rem] md:text-[1.5rem] lg:text-[2rem] text-[#212121] font-semibold font-fraunces`}
        >
          {heading}
        </h1>
      )}
      {sanitizedHtml !== '' && (
        <div
          className={`seo-content ${heading ? 'mt-0' : 'mt-6'} px-4 md:px-0 ${className ?? ''}`}
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
      )}
    </section>
  );
}
